import {Box, Button, Divider, Grid, Typography} from '@mui/material';
import {isPast} from 'date-fns';
import {FormikErrors} from 'formik';
import {set} from 'lodash-es';
import {useSnackbar} from 'notistack';
import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import AutoField, {
  ActionType,
  AutoTextField,
  DatePickerField,
  SelectField,
} from '../../components/AutoForm/AutoFieldNew';
import {AutoFormProps} from '../../components/AutoForm/AutoFormNew';
import {FixedFooter} from '../../components/Layout/Layout';
import {useLeadContext} from './LeadFormContext';
import useTitle from '../../hooks/useTitle';
import {useAutoForm, useAutoField} from '../../components/AutoForm/useAutoForm';
import {newUTCDate} from '../../utils/newUTCDate';
import {UPDATES} from '../../shared/UPDATES';

export type LeadFormPersonValues = {
  contactId: string;
  dob: string;
  firstName: string;
  middleName: string;
  homePhone: string;
  jobTitle: string;
  lastName: string;
  linkedinProfile: string;
  mobilePhone: string;
  nationalInsurance: string;
  primaryEmail: string;
  secondaryEmail: string;
  title: string;
  contactError: string;
};

type LeadAddress = {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  postcode: string;
  county: string;
  country: string;
  moveInDate: Date;
};

export type LeadFormValues = LeadAddress & {
  accountId: string;
  accountName: string;
  accountType: string;
  adviserId: string;
  companyName: string;
  relatedLeadId: string;
  leadId: string;
  leadSource: string;
  primary: LeadFormPersonValues;
  secondary?: LeadFormPersonValues;
  useSecondary: boolean;
  initialFee: string;
  initialFixedFee?: string;
  ongoingFee: string;
  // addresses: LeadAddress[];
};

const leadTypeField: SelectField = {
  id: 'accountType',
  label: 'Account type',
  type: ActionType.select,
  options: [
    {
      label: 'Household',
      value: 'Household',
    },
    {
      label: 'Trust',
      value: 'Trust',
    },
    {
      label: 'Corporate',
      value: 'Corporate',
    },
    {
      label: 'Pension scheme',
      value: 'Pension scheme',
    },
  ],
};
const titleField: SelectField = {
  id: 'title',
  label: 'Title',
  type: ActionType.select,
  options: [
    {
      label: 'Mr',
      value: 'mr',
    },
    {
      label: 'Ms',
      value: 'ms',
    },
  ],
};
const firstNameField: AutoTextField = {
  id: 'firstName',
  label: 'First Name',
  type: ActionType.text,
};
const lastNameField: AutoTextField = {
  id: 'lastName',
  label: 'Last Name',
  type: ActionType.text,
};
const knownAsField: AutoTextField = {
  id: 'knownAs',
  label: 'Known as',
  type: ActionType.text,
};
const emailField: AutoTextField = {
  id: 'primaryEmail',
  label: 'Email',
  type: ActionType.text,
  private: true,
};
const mobileField: AutoTextField = {
  id: 'mobilePhone',
  label: 'Mobile (tel)',
  type: ActionType.text,
  private: true,
};
// const homePhoneField: AutoTextField = {
//   id: 'homePhone',
//   label: 'Home (tel)',
//   type: ActionType.text,
// };
const nationalInsuranceField: AutoTextField = {
  id: 'nationalInsurance',
  label: 'National insurance',
  type: ActionType.text,
  private: true,
};

const dobField: DatePickerField = {
  id: 'dob',
  label: 'DOB (DD/MM/YYYY)',
  type: ActionType.datepicker,
  fullWidth: true,
  private: true,
};

const AddLeadPerson: React.FC<{personType: 'primary' | 'secondary'}> = ({personType}) => {
  return (
    <>
      <Grid item>
        <AutoField field={{...titleField, id: `${personType}.${titleField.id}`}} />
      </Grid>
      <Grid item>
        <AutoField
          field={{...firstNameField, id: `${personType}.${firstNameField.id}`}}
        />
      </Grid>
      <Grid item>
        <AutoField field={{...lastNameField, id: `${personType}.${lastNameField.id}`}} />
      </Grid>
      <Grid item>
        <AutoField field={{...knownAsField, id: `${personType}.${knownAsField.id}`}} />
      </Grid>
      <Grid item>
        <AutoField field={{...dobField, id: `${personType}.${dobField.id}`}} />
      </Grid>
      <Grid item>
        <AutoField
          field={{
            ...nationalInsuranceField,
            id: `${personType}.${nationalInsuranceField.id}`,
          }}
        />
      </Grid>
      <Box mt={2} />
      <Grid item>
        <Typography variant="h6">
          {personType === 'primary' ? 'Primary' : 'Secondary'} contact details (one is
          required):
        </Typography>
      </Grid>
      <Grid item>
        <AutoField field={{...emailField, id: `${personType}.${emailField.id}`}} />
      </Grid>
      <Grid item>
        <AutoField field={{...mobileField, id: `${personType}.${mobileField.id}`}} />
      </Grid>
      {/* <Grid item>
        <AutoField
          field={{...homePhoneField, id: `${personType}.${homePhoneField.id}`}}
        />
      </Grid> */}
    </>
  );
};

const validatePerson = (
  person: LeadFormPersonValues,
  primary: boolean,
  errors: FormikErrors<LeadFormValues>
): FormikErrors<LeadFormValues> => {
  const prefix = primary ? 'primary' : 'secondary';
  if (!person.title) {
    set(errors, `${prefix}.title`, 'Required');
  }
  if (!person.firstName) {
    set(errors, `${prefix}.firstName`, 'Required');
  }
  if (!person.lastName) {
    set(errors, `${prefix}.lastName`, 'Required');
  }
  if (!person.nationalInsurance) {
    set(errors, `${prefix}.nationalInsurance`, 'Required');
  }
  if (!person.dob) {
    set(errors, `${prefix}.dob`, 'Required');
    // errors.moveInDate = 'Required';
  } else if (isPast(newUTCDate(person.dob))) {
    // all good
  } else {
    set(errors, `${prefix}.dob`, 'Must be in the past');
  }
  if (!person.primaryEmail && !person.homePhone && !person.mobilePhone) {
    set(errors, `${prefix}.contactError`, 'At least one is required');
    set(errors, `${prefix}.primaryEmail`, 'Required');
    // set(errors, `${prefix}.homePhone`, 'Required');
    set(errors, `${prefix}.mobilePhone`, 'Required');
  }

  return errors;
};

const validateBasic = (values: LeadFormValues): FormikErrors<LeadFormValues> => {
  const errors: FormikErrors<LeadFormValues> = {};
  if (!values.accountType) {
    set(errors, 'accountType', 'Required');
  }
  if (!values.primary) {
    set(errors, `primary.firstName`, 'Required');
  } else {
    validatePerson(values.primary, true, errors);
  }

  return errors;
};

export const LeadBasicForm: React.FC<AutoFormProps<LeadFormValues>> = () => {
  useTitle(`Add Lead`);
  const navigate = useNavigate();
  const {closeSnackbar, enqueueSnackbar} = useSnackbar();
  const {goToStep, onCreateOrUpdate, onCreateOrUpdateLoading, saving} = useLeadContext();
  const {values, setErrors} = useAutoForm<LeadFormValues>();
  const [showSecondary, _, {setValue: setShowSecondary}] =
    useAutoField<LeadFormValues['useSecondary']>('useSecondary');

  useEffect(() => {
    goToStep('basic');
  }, []);

  return (
    <>
      <Box mt={1} mb={1} padding={3} bgcolor="white">
        <Grid container spacing={2}>
          <Grid item xs={12} md={8} lg={6}>
            <Grid container direction="column" spacing={1}>
              <Grid item>
                <AutoField field={leadTypeField} />
              </Grid>
              <Box mt={4} />
              <Grid item>
                <Typography variant="h6">Primary client details:</Typography>
              </Grid>
              <AddLeadPerson personType="primary" />
              {showSecondary.value ? (
                <>
                  <Box mt={4} />
                  <Divider />
                  <Box mt={4} />
                  <Grid item>
                    <Typography variant="h6">Secondary client details:</Typography>
                  </Grid>
                  <AddLeadPerson personType="secondary" />
                  <Box mt={2} />
                  <Grid item>
                    <Button
                      onClick={() => setShowSecondary(UPDATES._1611, false)}
                      variant="outlined"
                      color="secondary"
                    >
                      Remove secondary
                    </Button>
                  </Grid>
                </>
              ) : (
                <>
                  <Box mt={2} />
                  {/* <Grid item>
                  <Button
                    onClick={() => setShowSecondary(true)}
                    variant="contained"
                    color="primary"
                  >
                    Add secondary person
                  </Button>
                </Grid> */}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Box mt={4}>
          <FixedFooter>
            <Grid justifyContent="space-between" container>
              <div />
              <Button
                onClick={() => {
                  closeSnackbar();
                  const newErrors = validateBasic(values);
                  setErrors(newErrors);
                  if (Object.keys(newErrors).length) {
                    enqueueSnackbar(
                      `Some fields are missing. Please complete before saving.`,
                      {variant: 'error'}
                    );
                  } else {
                    onCreateOrUpdate(values).then(() => {
                      navigate('/search');
                    });
                  }
                }}
                variant="contained"
                color="primary"
              >
                {saving ? 'Saving...' : 'Save and exit'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={onCreateOrUpdateLoading}
                onClick={() => {
                  closeSnackbar();
                  const newErrors = validateBasic(values);
                  setErrors(newErrors);
                  if (Object.keys(newErrors).length) {
                    enqueueSnackbar(
                      `Some fields are missing. Please complete before saving.`,
                      {variant: 'error'}
                    );
                  } else {
                    onCreateOrUpdate(values).then((leadId) =>
                      navigate(`/leads/${leadId}/address`)
                    );
                  }
                }}
              >
                {saving ? 'Saving...' : 'Next'}
              </Button>
            </Grid>
          </FixedFooter>
        </Box>
      </Box>
    </>
  );
};

// const AddLeadFormWrapped = withAutoFormNew<LeadFormValues, {}>(div);

// export const AddLead: React.FC = () => {
//   const {lead, onCreateOrUpdate} = useLeadContext();
//   const navigate = useNavigate();
//   useScrollToTop();
//   const initialValueOverrides: Partial<LeadFormValues> = useMemo(() => {
//     return {
//       accountId: lead?.account?.id || undefined,
//       accountName: lead?.accountName || undefined,
//       accountType: lead?.accountType || undefined,

//       companyName: lead?.companyName || undefined,
//       contactId: lead?.contact?.id || undefined,
//       dob: lead?.dob || undefined,
//       firstName: lead?.firstName || undefined,
//       lastName: lead?.lastName || undefined,
//       leadSource: lead?.leadSource || undefined,
//       jobTitle: lead?.jobTitle || undefined,
//       // adviserId: lead?.adviserId || undefined,
//       homePhone: lead?.homePhone || undefined,
//       mobilePhone: lead?.mobilePhone || undefined,
//       leadId: lead?.id || undefined,
//       linkedinProfile: lead?.linkedinProfile || undefined,
//       nationalInsurance: lead?.nationalInsurance || undefined,
//       primaryEmail: lead?.primaryEmail || undefined,
//       secondaryEmail: lead?.secondaryEmail || undefined,
//       title: lead?.title || undefined,
//       // addresses
//       addresses: [
//         {
//           address1: '',
//           address2: '',
//           address3: '',
//           county: '',
//           moveInDate: newUTCDate(),
//           postcode: '',
//           town: '',
//         },
//       ],
//     };
//   }, [lead]);

//   return (
//     <>
//       <AddLeadFormWrapped
//         submit={(values) => {
//           navigate(`../address`);
//           // onCreateOrUpdate(values).then((leadId) => {
//           //   navigate(`./address?leadId=${leadId}`);
//           // });
//         }}
//         formFields={[]}
//         initialValueOverrides={initialValueOverrides}
//       />
//     </>
//   );
// };
